import { Component, inject, signal, computed, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AvatarModule } from 'primeng/avatar';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { ClientService, Client, ClientDto } from '../../services/client';
import { FileService } from '../../services/file';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { DocUploadComponent } from '../../shared/doc-upload';

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    ToggleSwitchModule,
    IconFieldModule,
    InputIconModule,
    AvatarModule,
    ChipModule,
    DividerModule,
    DocUploadComponent,
  ],
  templateUrl: './clients.html',
  styleUrls: ['./clients-shared.css', './clients.css'],
})
export class Clients implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private svc = inject(ClientService);
  private fileSvc = inject(FileService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  /** Resolved presigned URLs for logo guids, keyed by guid (so the grid avatar renders). */
  logoUrls = signal<Record<string, string>>({});

  readonly totalCount = computed(() => this.rows().length);
  readonly activeCount = computed(() => this.rows().filter((c) => c.isActive).length);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<Client[]>([]);

  private searchTimer: any;

  form: FormGroup = this.fb.group({
    id: [0],
    clientCode: ['', Validators.required],
    clientName: ['', Validators.required],
    companyName: [''],
    contactPerson: [''],
    email: ['', Validators.email],
    mobile: ['', Validators.pattern(/^\d{10}$/)],
    address: [''],
    city: [''],
    state: [''],
    country: [''],
    pincode: ['', Validators.pattern(/^\d{6}$/)],
    gstNo: ['', Validators.pattern(/^[0-9A-Z]{15}$/)],
    panNo: ['', Validators.pattern(/^[A-Z]{5}[0-9]{4}[A-Z]$/)],
    logoUrl: [''], // holds the uploaded file's S3 guid (or a legacy http URL)
    website: ['', Validators.pattern(/^https?:\/\/.+/i)],
    remarks: [''],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    // Deep-link from the detail page's "Edit" action: /clients?edit=:id opens the edit dialog.
    const editId = Number(this.route.snapshot.queryParamMap.get('edit'));
    if (editId) {
      this.svc.cltGetById(editId).subscribe({
        next: (res: any) => {
          const c = res?.data;
          if (c?.id) this.openEdit(c);
        },
      });
    }
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    this.svc.cltGetAll(this.search().trim() || undefined).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        const list: Client[] = Array.isArray(data) ? data : [];
        this.rows.set(list);
        this.resolveLogos(list);
      },
      error: () => this.loading.set(false),
    });
  }

  /** A logo stored as an S3 guid → presigned URL for the grid avatar (http URLs render as-is). */
  private resolveLogos(list: Client[]) {
    for (const c of list) {
      const v = (c.logoUrl ?? '').trim();
      if (!v || /^https?:\/\//i.test(v) || this.logoUrls()[v]) continue;
      this.fileSvc.url(v).subscribe({
        next: (u) => {
          if (u) this.logoUrls.update((m) => ({ ...m, [v]: u }));
        },
      });
    }
  }

  /** Render URL for a row's logo: a direct http URL, the resolved presigned URL, or '' (fallback). */
  logoSrc(c: Client): string {
    const v = (c.logoUrl ?? '').trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    return this.logoUrls()[v] ?? '';
  }

  /** Debounced server-side search (code / name / company / email / mobile). */
  onSearch(v: string) {
    this.search.set(v);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(), 350);
  }

  // ===================== navigation =====================
  open(c: Client) {
    this.router.navigate(['/clients', c.id]);
  }

  initials(name?: string | null): string {
    const n = (name || '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }

  /** Force certain fields to uppercase as the user types (code / GST / PAN). */
  upper(controlName: string) {
    const ctrl = this.form.get(controlName);
    if (!ctrl) return;
    const v = (ctrl.value ?? '').toString().toUpperCase();
    if (v !== ctrl.value) ctrl.setValue(v, { emitEvent: false });
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      clientCode: '',
      clientName: '',
      companyName: '',
      contactPerson: '',
      email: '',
      mobile: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      gstNo: '',
      panNo: '',
      logoUrl: '',
      website: '',
      remarks: '',
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(c: Client) {
    this.editing.set(true);
    this.form.reset({
      id: c.id,
      clientCode: c.clientCode ?? '',
      clientName: c.clientName ?? '',
      companyName: c.companyName ?? '',
      contactPerson: c.contactPerson ?? '',
      email: c.email ?? '',
      mobile: c.mobile ?? '',
      address: c.address ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      country: c.country ?? '',
      pincode: c.pincode ?? '',
      gstNo: c.gstNo ?? '',
      panNo: c.panNo ?? '',
      logoUrl: c.logoUrl ?? '',
      website: c.website ?? '',
      remarks: c.remarks ?? '',
      isActive: c.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('clientCode')?.invalid) this.toast.warning('Client code is required.');
      else if (this.form.get('clientName')?.invalid) this.toast.warning('Client name is required.');
      else this.toast.warning('Please fix the highlighted fields.');
      return;
    }

    const v = this.form.getRawValue();
    const dto: ClientDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientCode: (v.clientCode ?? '').toString().trim().toUpperCase(),
      clientName: (v.clientName ?? '').toString().trim(),
      companyName: this.nz(v.companyName),
      contactPerson: this.nz(v.contactPerson),
      email: this.nz(v.email),
      mobile: this.nz(v.mobile),
      address: this.nz(v.address),
      city: this.nz(v.city),
      state: this.nz(v.state),
      country: this.nz(v.country),
      pincode: this.nz(v.pincode),
      gstNo: this.nz(v.gstNo),
      panNo: this.nz(v.panNo),
      logoUrl: this.nz(v.logoUrl),
      website: this.nz(v.website),
      remarks: this.nz(v.remarks),
      isActive: !!v.isActive,
    } as ClientDto;

    this.saving.set(true);
    this.svc.cltSave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          // 409 on duplicate code surfaces as success=false with the backend message.
          if (res?.statusCode === 409) this.toast.error('Client code already exists');
          else this.toast.error(res?.message || 'Failed to save client');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Client updated' : 'Client created'));
        this.closeModal();
        this.load();
      },
      error: (err: any) => {
        this.saving.set(false);
        if (err?.status === 409) this.toast.error('Client code already exists');
        else this.toast.error(err?.error?.message || 'Failed to save client');
      },
    });
  }

  // ===================== row actions =====================
  toggleActive(c: Client) {
    const next = !c.isActive;
    this.patchRow(c.id, { isActive: next });
    this.svc.cltSetActive(c.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(c.id, { isActive: c.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(c.id, { isActive: c.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  async remove(c: Client) {
    const ok = await this.toast.confirm(
      `Client "${c.clientName}" (${c.clientCode}) will be deleted.`,
      'Delete client?',
      'Delete',
    );
    if (!ok) return;
    this.svc.cltDelete(c.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete client');
          return;
        }
        this.toast.success(res?.message || 'Client deleted');
        this.load();
      },
    });
  }

  // ===================== utils =====================
  private patchRow(id: number, patch: Partial<Client>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  private nz(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
